import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setSearchQuery, fetchGames } from "../../redux/slice/game.slice";
import { addToCart } from "../../redux/slice/user.slice";
import { FaWindows } from 'react-icons/fa';
import TrendingTabs from "./TrendingTabs";
import GameCard, { GameCardSkeleton, PlatformIcon, PlatformColor, PlatformLabel } from "../common/GameCard";
import PeekingCartoonCharacter from "./PeekingCartoonCharacter";
import useGridCorners from "../../hooks/useGridCorners";

export default function RecommendedGames() {
  const dispatch = useDispatch();
  const { games, searchQuery, selectedCategory, activeTab, loading: gamesLoading } = useSelector(
    (s) => s.game,
  );
  const homeData = useSelector((s) => s.home);

  const [platformSelectorGame, setPlatformSelectorGame] = useState(null);
  const [selectedPlatformForGame, setSelectedPlatformForGame] = useState("");

  useEffect(() => {
    if (selectedCategory === "All" && !searchQuery) return;
    dispatch(
      fetchGames({
        category: selectedCategory !== "All" ? selectedCategory : undefined,
        search: searchQuery || undefined,
        limit: 12,
      }),
    );
  }, [selectedCategory, searchQuery, dispatch]);

  const handleAddToCartClick = (game) => {
    const available = Object.entries(game.platforms || {}).filter(
      ([_, info]) => info?.available,
    );
    if (available.length === 1) {
      dispatch(addToCart({ ...game, selectedPlatform: available[0][0] }));
    } else if (available.length > 1) {
      setPlatformSelectorGame(game);
      const keys = available.map((a) => a[0]);
      setSelectedPlatformForGame(
        keys.includes("windows") ? "windows" : keys[0],
      );
    } else {
      dispatch(addToCart({ ...game, selectedPlatform: "windows" }));
    }
  };

  let baseGames = games;

  if (selectedCategory === "All" && !searchQuery) {
    let tabData = [];
    switch (activeTab) {
      case "Trending":
        tabData = homeData.trending || [];
        break;
      case "New Releases":
        tabData = homeData.newRelease || [];
        break;
      case "Top Selling":
        tabData = homeData.topSelling || [];
        break;
      case "Most Played":
        tabData = homeData.mostPlayed || [];
        break;
      case "Upcoming":
        tabData = homeData.upcoming || [];
        break;
      default:
        tabData = games;
    }
    // Fallback to non-empty pool if specific tab data is empty
    const poolFallback = (homeData.trending?.length ? homeData.trending : (homeData.newRelease?.length ? homeData.newRelease : games)) || [];
    baseGames = tabData.length > 0 ? tabData : poolFallback;
  }

  // Dynamic Filtering Logic
  const filteredGames = baseGames.filter((game) => {
    // 1. Filter by search query (match title, tagline, developer, genre, or tags)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchTitle = game.title?.toLowerCase().includes(q) || false;
      const matchDesc = (game.description || game.tagline || "")
        .toLowerCase()
        .includes(q);
      const matchGenre = game.genre?.toLowerCase().includes(q) || false;
      const matchCat = game.category?.name?.toLowerCase().includes(q) || false;
      const matchTags =
        game.tags?.some((t) => t.toLowerCase().includes(q)) || false;
      if (!matchTitle && !matchDesc && !matchCat && !matchTags) return false;
    }

    // 2. Filter by Category
    if (selectedCategory !== "All") {
      const isMatch = game.category?.name === selectedCategory;
      if (!isMatch) return false;
    }

    return true;
  });

  const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ defaultCols: 5 });

  const isLoading = gamesLoading || homeData?.loading || (!games?.length && !homeData?.trending?.length && !homeData?.newRelease?.length);

  return (
    <section
      id="store-section"
      className="py-8 sm:py-12 relative scroll-mt-20 overflow-x-clip"
    >
      {/* Background Neon Blob Orbs */}
      <div className="absolute right-0 top-1/4 w-80 h-80 bg-cyber-cyan/5 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute left-0 bottom-1/4 w-80 h-80 bg-cyber-pink/5 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="w-full container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center relative z-10">
        {/* ── Section Header (Standardized with Categories, FAQs & Legal) ── */}
        <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center mb-6 sm:mb-8">
          <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2 justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
            Featured Catalog
          </p>
          <h1 className="font-cyber font-semibold text-2xl xs:text-2xl sm375:text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight leading-tight">
            THE NEXT-GEN <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)] inline-block">STOREFRONT</span>
          </h1>
          <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full mx-auto"></div>
          <p className="text-gray-400 font-sans max-w-lg text-xs sm:text-sm mx-auto mt-3.5 leading-relaxed">
            Discover verified digital keys, top-rated trending releases, and best deals across all gaming platforms.
          </p>
        </div>

        {/* Tab switching is only relevant if no specific genre category filter is locked */}
        {selectedCategory === "All" && (
          <div
            id="trending-section"
            className="mb-6 sm:mb-8 w-full flex justify-center"
          >
            <TrendingTabs />
          </div>
        )}

        {/* Games Grid Container with Peeking Cartoon Character behind cards */}
        <div className="relative w-full">
          {isLoading ? (
            <div ref={setGridRef} className="grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-5 lg:gap-6 w-full max-w-sm sm500:max-w-none mx-auto relative z-10">
              {[...Array(10)].map((_, index) => {
                let visibilityClass = "block";
                if (index >= 4 && index < 6) {
                  visibilityClass = "hidden sm500:block";
                } else if (index >= 6 && index < 8) {
                  visibilityClass = "hidden lg:block";
                } else if (index >= 8) {
                  visibilityClass = "hidden 2xl:block";
                }

                return (
                  <div key={index} className={`${visibilityClass} h-full`}>
                    <GameCardSkeleton style={getCornerStyle(index, 10, gridCols)} />
                  </div>
                );
              })}
            </div>
          ) : filteredGames.length > 0 ? (
            <div ref={setGridRef} className="grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-5 lg:gap-6 w-full max-w-sm sm500:max-w-none mx-auto relative z-10">
              {filteredGames.slice(0, 10).map((game, index) => {
                let visibilityClass = "block";
                if (index >= 4 && index < 6) {
                   visibilityClass = "hidden sm500:block";
                } else if (index >= 6 && index < 8) {
                  visibilityClass = "hidden lg:block";
                } else if (index >= 8) {
                  visibilityClass = "hidden 2xl:block";
                }

                return (
                  <div key={game.id || game._id} className={`${visibilityClass} h-full`}>
                    <GameCard
                      game={game}
                      onAddToCart={() => handleAddToCartClick(game)}
                      style={getCornerStyle(index, Math.min(filteredGames.length, 10), gridCols)}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty Search Fallback */
            <div className="w-full max-w-lg bg-[#0b101f]/80 backdrop-blur-2xl border border-cyber-pink/30 p-8 sm:p-12 rounded-2xl flex flex-col items-center text-center space-y-4 sm:space-y-6 my-8 sm:my-12 mx-auto shadow-[0_0_30px_rgba(255,0,122,0.15)] relative z-10">
              <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-cyber-pink/10 border border-cyber-pink/30 flex items-center justify-center">
                <span className="text-2xl sm:text-3xl text-cyber-pink animate-ping absolute opacity-40">
                  !
                </span>
                <span className="text-2xl sm:text-3xl text-cyber-pink font-extrabold relative">
                  !
                </span>
              </div>
              <div>
                <h3 className="font-cyber font-bold text-lg sm:text-xl text-white uppercase tracking-wider">
                  NO GAMES FOUND
                </h3>
                <p className="text-gray-400 text-xs sm:text-sm mt-2 font-mono px-2 sm:px-0">
                  No active games match your query. Try searching for another
                  title or category.
                </p>
              </div>
              <button
                onClick={() => {
                  dispatch(setSearchQuery(""));
                }}
                className="cyber-btn px-5 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm"
              >
                RESET SEARCH
              </button>
            </div>
          )}

          {/* Peeking Cartoon Character positioned behind the card */}
          <PeekingCartoonCharacter />
        </div>
      </div>

      {/* Platform Selector Modal */}
      {platformSelectorGame && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
          <div className="w-full max-w-md bg-[#0c0c18] border border-cyber-cyan/30 rounded-[2.5rem] p-8 shadow-[0_0_30px_rgba(0,240,255,0.15)] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink"></div>

            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-xl font-cyber font-black text-white uppercase tracking-tight">
                  Select Platform
                </h3>
                <p className="text-gray-400 text-xs font-medium">
                  Choose your target platform for{" "}
                  <span className="text-cyber-cyan">
                    {platformSelectorGame.title}
                  </span>
                </p>
              </div>

              <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                {Object.entries(platformSelectorGame.platforms || {})
                  .filter(([_, info]) => info?.available)
                  .map(([key, info]) => {
                    const isSelected = selectedPlatformForGame === key;
                    const Icon = PlatformIcon[key] || FaWindows;
                    const label = PlatformLabel[key] || key;
                    const brandColor = PlatformColor[key] || '#9ca3af';
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedPlatformForGame(key)}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all text-left
                          ${isSelected
                            ? "bg-cyber-cyan/10 border-cyber-cyan text-white shadow-[0_0_15px_rgba(0,240,255,0.1)]"
                            : "bg-white/[0.02] border-white/5 text-gray-400 hover:text-white hover:bg-white/[0.04]"
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-4 h-4" style={{ color: brandColor }} />
                          <span className="text-xs font-cyber font-bold uppercase tracking-wide">
                            {label}
                          </span>
                        </div>
                        <span className="text-xs font-mono font-bold text-white">
                          {info.price === 0 ? "FREE" : `$${info.price}`}
                        </span>
                      </button>
                    );
                  })}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setPlatformSelectorGame(null);
                    setSelectedPlatformForGame("");
                  }}
                  className="flex-1 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    dispatch(
                      addToCart({
                        ...platformSelectorGame,
                        selectedPlatform: selectedPlatformForGame,
                      }),
                    );
                    setPlatformSelectorGame(null);
                    setSelectedPlatformForGame("");
                  }}
                  disabled={!selectedPlatformForGame}
                  className="flex-1 py-3.5 rounded-2xl bg-cyber-cyan/20 border border-cyber-cyan/40 text-cyber-cyan font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-cyan/30 disabled:opacity-40 transition-all"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
